import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoSearchOutline,
  IoFilterOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoGitNetworkOutline,
  IoTimeOutline,
  IoPlayOutline,
  IoCheckmarkCircleOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";
import { BiPlus } from "react-icons/bi";
import DebounceInput from "../../../../../components/DebounceInput";
import MultiSelect from "../../../../../components/MultiSelect";
import API from "../../../../../services/api";

const BacklogFilters = ({ filters, setFilters, project, showRoadmap, setShowRoadmap, setIsCreateTicketModalOpen }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [modules, setModules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [budgets, setBudgets] = useState([]);

  // Fetch modules
  useEffect(() => {
    async function fetchModules() {
      try {
        const { data, ok } = await API.post("/roadlog_module/search", { project_id: project._id });
        if (ok) setModules(data);
      } catch (error) {
        console.error("Error fetching modules:", error);
      }
    }
    fetchModules();
  }, [project._id]);

  // Fetch assignees
  useEffect(() => {
    async function fetchAssignees() {
      try {
        const { ok, data } = await API.post(`/user/search`, { project_ids: [project._id], status: "active", per_page: 100 });
        if (ok) setAssignees(data.users);
      } catch (error) {
        console.error("Error fetching assignees:", error);
      }
    }
    fetchAssignees();
  }, [project._id]);

  // Fetch budgets
  useEffect(() => {
    async function fetchBudgets() {
      try {
        const { data, ok } = await API.post("/budget/search", { projectId: project._id, status: "active" });
        if (ok) setBudgets(data);
      } catch (error) {
        console.error("Error fetching budgets:", error);
      }
    }
    fetchBudgets();
  }, [project._id]);

  // Get unique categories from current items
  useEffect(() => {
    async function fetchCategories() {
      try {
        const { data, ok } = await API.post("/roadlog_item/search", {
          project_id: project._id,
          is_in_backlog: "yes",
        });
        if (ok) {
          const uniqueCategories = [...new Set(data.map((item) => item.category).filter(Boolean))];
          setCategories(uniqueCategories.map((cat) => ({ value: cat, label: cat })));
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    }
    fetchCategories();
  }, [project._id]);

  const clearFilters = () => {
    setFilters({
      search: "",
      categories: [],
      module_ids: [],
      users: [],
      roadlog_tags: [],
      statuses: [],
      resource_profile: [],
      budget_id: null,
      nnls: [],
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "TODO":
        return <IoTimeOutline className="h-3.5 w-3.5" />;
      case "IN_PROGRESS":
        return <IoPlayOutline className="h-3.5 w-3.5" />;
      case "DONE":
        return <IoCheckmarkCircleOutline className="h-3.5 w-3.5" />;
      case "NEED_INFO":
        return <IoInformationCircleOutline className="h-3.5 w-3.5" />;
      default:
        return null;
    }
  };

  const getNNLClass = (nnlValue) => {
    switch (nnlValue) {
      case "NOW":
        return "bg-blue-50 text-blue-700";
      case "NEXT":
        return "bg-yellow-50 text-yellow-700";
      case "LATER":
        return "bg-gray-50 text-gray-700";
      default:
        return "";
    }
  };

  return (
    <div className="bg-white border border-neutral-300 rounded-lg">
      {/* Main Filter Bar */}
      <div className="p-2 flex items-center gap-2.5">
        {/* Roadmap Toggle Button */}
        <button
          onClick={() => setShowRoadmap(!showRoadmap)}
          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded-md transition-all duration-200 shadow-sm flex-shrink-0
            ${
              showRoadmap
                ? "bg-blue-50 text-blue-700 border-blue-200 shadow-blue-100"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300"
            }`}
          title={showRoadmap ? "Hide roadmap" : "Show roadmap"}>
          <IoGitNetworkOutline className="h-4 w-4" />
        </button>

        {/* Search */}
        <div className="relative flex-1 min-w-0">
          <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
            <IoSearchOutline className="h-3.5 w-3.5 text-gray-400" />
          </div>
          <DebounceInput
            debounce={300}
            className="block w-full pl-9 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400 bg-gray-50 focus:bg-white transition-colors"
            placeholder="Search backlog items..."
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          />
        </div>

        {/* Category Filter */}
        <div className="w-44 flex-shrink-0">
          <MultiSelect
            id="select-category"
            options={categories}
            values={filters.categories?.map((cat) => ({ value: cat, label: cat }))}
            onSelectedChange={(selected) => setFilters((f) => ({ ...f, categories: selected.map((s) => s.value) }))}
            placeholder="Category"
            buttonClassName="px-2.5 py-1.5 text-xs bg-gray-50 hover:bg-white border-gray-200 rounded-lg transition-colors"
          />
        </div>

        {/* Module Filter */}
        <div className="w-44 flex-shrink-0">
          <MultiSelect
            id="select-module-backlog"
            options={modules.map((m) => ({ value: m._id, label: m.title }))}
            values={filters.module_ids
              ?.map((id) => {
                const module = modules.find((m) => m._id === id);
                return module ? { value: id, label: module.title } : null;
              })
              .filter(Boolean)}
            onSelectedChange={(selected) => setFilters((f) => ({ ...f, module_ids: selected.map((s) => s.value) }))}
            placeholder="Module"
            buttonClassName="px-2.5 py-1.5 text-xs bg-gray-50 hover:bg-white border-gray-200 rounded-lg transition-colors"
          />
        </div>

        {/* Assignee Filter */}
        <div className="w-44 flex-shrink-0">
          <MultiSelect
            id="select-assignee-backlog"
            options={
              assignees.length > 0
                ? assignees.map((user) => ({
                    value: user._id,
                    label: user.name || user.email.split(/[@.]/)[0],
                  }))
                : []
            }
            values={filters.users
              ?.map((id) => {
                const user = assignees.find((u) => u._id === id);
                return user
                  ? {
                      value: id,
                      label: user.name,
                    }
                  : null;
              })
              .filter(Boolean)}
            onSelectedChange={(selected) => setFilters((f) => ({ ...f, users: selected.map((s) => s.value) }))}
            placeholder="Assignee"
            buttonClassName="px-2.5 py-1.5 text-xs bg-gray-50 hover:bg-white border-gray-200 rounded-lg transition-colors"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border rounded-lg transition-all duration-200 shadow-sm
              ${
                showFilters
                  ? "bg-blue-50 text-blue-700 border-blue-200 shadow-blue-100"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300"
              }`}>
          <IoFilterOutline className="h-3.5 w-3.5" />
          <span>Filters</span>
          <IoChevronDownOutline className={`h-3 w-3 transition-transform duration-200 ${showFilters ? "rotate-180" : ""}`} />
        </button>

        {/* Toggle More Filters & Clear */}
        <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-gray-200">
          <button
            onClick={() => setIsCreateTicketModalOpen(true)}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 rounded-lg transition-all duration-200 shadow-sm">
            <BiPlus />
            <span>New Item</span>
          </button>

          <button
            onClick={clearFilters}
            className="px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:text-red-600 border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-200 transition-all duration-200 shadow-sm">
            Clear
          </button>
        </div>
      </div>

      {/* Expandable Filters Section */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="border-t border-gray-100">
            <div className="p-3">
              <div className="grid grid-cols-5 gap-3">
                {/* Tags Filter */}
                <div className="w-full">
                  <SelectTag value={filters.roadlog_tags} onChange={(tags) => setFilters((f) => ({ ...f, roadlog_tags: tags }))} project={project} />
                </div>

                {/* Status Filter */}
                <div className="w-full">
                  <MultiSelect
                    id="select-status-backlog"
                    options={["TODO", "READY_TO_DEV", "IN_PROGRESS", "TESTING", "DONE", "ARCHIVED"].map((status) => ({
                      value: status,
                      label: status,
                      icon: getStatusIcon(status),
                    }))}
                    values={filters.statuses?.map((status) => ({ value: status, label: status }))}
                    onSelectedChange={(selected) => setFilters((f) => ({ ...f, statuses: selected.map((s) => s.value) }))}
                    placeholder="Status"
                    buttonClassName="px-2.5 py-1.5 text-xs bg-gray-50 hover:bg-white border-gray-200 rounded-lg transition-colors"
                  />
                </div>

                {/* Resource Profile Filter */}
                <div className="w-full">
                  <MultiSelect
                    id="select-resource-profile-backlog"
                    options={["DEVELOPER", "EXPERIMENTED DEVELOPER", "CTO", "PRODUCT", "IA ENGINEER", "DevOps"].map((profile) => ({
                      value: profile,
                      label: profile,
                    }))}
                    values={filters.resource_profile?.map((profile) => ({ value: profile, label: profile }))}
                    onSelectedChange={(selected) => setFilters((f) => ({ ...f, resource_profile: selected.map((s) => s.value) }))}
                    placeholder="Resource Profile"
                    buttonClassName="px-2.5 py-1.5 text-xs bg-gray-50 hover:bg-white border-gray-200 rounded-lg transition-colors"
                  />
                </div>

                {/* Budget Filter */}
                <div className="w-full">
                  <select
                    value={filters.budget_id || ""}
                    onChange={(e) => setFilters((f) => ({ ...f, budget_id: e.target.value || null }))}
                    className="w-full px-2.5 py-1.5 text-xs bg-gray-50 hover:bg-white border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors">
                    <option value="">All budgets</option>
                    {budgets.map((budget) => (
                      <option key={budget._id} value={budget._id}>
                        {budget.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* NNL (Now/Next/Later) Filter */}
                <div className="w-full">
                  <MultiSelect
                    id="select-nnl-backlog"
                    options={["NOW", "NEXT", "LATER"].map((nnl) => ({
                      value: nnl,
                      label: nnl,
                      className: getNNLClass(nnl),
                    }))}
                    values={filters.nnls?.map((nnl) => ({ value: nnl, label: nnl }))}
                    onSelectedChange={(selected) => setFilters((f) => ({ ...f, nnls: selected.map((s) => s.value) }))}
                    placeholder="NNL"
                    buttonClassName="px-2.5 py-1.5 text-xs bg-gray-50 hover:bg-white border-gray-200 rounded-lg transition-colors"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SelectTag = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);

  async function fetchTags() {
    const { data } = await API.post("/roadlog_tag/search", { project_id: project._id, sort: "name" });
    setOptions(data);
  }

  useEffect(() => {
    fetchTags();
  }, [project._id]);

  const selectedValues = Array.isArray(value) ? value.map((v) => ({ value: v._id, label: v.name })) : value ? [{ value: value._id, label: value.name }] : [];
  return (
    <MultiSelect
      id="select-tag"
      options={options.map((o) => ({ value: o._id, label: o.name }))}
      values={selectedValues}
      onSelectedChange={(selected) => onChange(selected.map((s) => ({ _id: s.value, name: s.label })))}
      placeholder="Tag"
      buttonClassName="px-2.5 py-1.5 text-xs bg-gray-50 hover:bg-white border-gray-200 rounded-lg transition-colors"
      onOpen={() => fetchTags()}
    />
  );
};

export default BacklogFilters;
